/**
 * useGuideTour
 * Hook för att hantera onboarding-guider med react-joyride.
 *
 * Hanterar:
 * 1. Hämtning av preferences – har användaren sett turen?
 * 2. Hämtning av guide-steg från API
 * 3. Automatisk start (autoStart=true) om turen ej är sedd
 * 4. Sparar sett-status i preferences när turen avslutas
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Step, CallBackProps, Status } from 'react-joyride';
import { STATUS } from 'react-joyride';

interface ApiGuideStep {
  id: number;
  tour_id: number;
  step_order: number;
  target_selector: string | null;
  title: string | null;
  content: string | null;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  is_active: boolean;
}

interface ApiGuideTour {
  id: number;
  tour_key: string;
  name: string;
  description: string | null;
  is_active: boolean;
  steps: ApiGuideStep[];
}

interface UserPreferences {
  guideTours?: Record<string, string | null>;
  [key: string]: unknown;
}

async function fetchPreferences(): Promise<UserPreferences> {
  const res = await fetch('/api/users/me/preferences', { credentials: 'include' });
  if (!res.ok) return {};
  const json = await res.json();
  return json.data ?? {};
}

async function savePreferences(updates: Partial<UserPreferences>): Promise<void> {
  await fetch('/api/users/me/preferences', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

async function fetchTour(tourKey: string): Promise<ApiGuideTour | null> {
  // Hämta alla aktiva turer (ett anrop) i stället för GET …/tours/:key — undviker 404 i konsolen
  // när en tur saknas i DB (gammal backend returnerade 404; nginx visar det som rött i DevTools).
  const res = await fetch('/api/guides/tours', { credentials: 'include' });
  if (!res.ok) return null;
  const json = await res.json();
  const list = json.data as ApiGuideTour[] | undefined;
  if (!Array.isArray(list)) return null;
  const tour = list.find((t) => t.tour_key === tourKey);
  if (!tour || !Array.isArray(tour.steps) || tour.steps.length === 0) return null;
  return tour;
}

function mapToJoyrideStep(step: ApiGuideStep): Step {
  const hasTarget = !!step.target_selector;
  return {
    target: step.target_selector ?? 'body',
    title: step.title ?? undefined,
    content: step.content ?? '',
    placement: hasTarget ? step.placement : 'center',
    // disableBeacon BARA för center-steg (body-target).
    // För steg med specifika selektorer låter vi Joyride hantera saknade element
    // via spotlightPadding och utan beacon-blockering.
    disableBeacon: !hasTarget,
    hideFooter: false,
    spotlightPadding: hasTarget ? 8 : 0,
  };
}

// ─────────────────────────────────────────────────────────────

export interface UseGuideTourReturn {
  /** Kör-tillstånd för joyride */
  run: boolean;
  /** Joyride-steg (mappade från DB) */
  steps: Step[];
  /** Nyckel för att tvinga ommontering av Joyride (för omstart) */
  joyrideKey: number;
  /** Om turen är laddad och redo */
  isReady: boolean;
  /** Om användaren redan har sett turen */
  hasSeenTour: boolean;
  /** Starta turen manuellt */
  startTour: () => void;
  /** Återställ (starta om från steg 0) */
  restartTour: () => void;
  /** Callback som skickas till Joyride-komponenten */
  handleJoyrideCallback: (data: CallBackProps) => void;
}

/**
 * @param tourKey - Nyckeln för turen, t.ex. 'welcome' eller 'module_fleet'
 * @param autoStart - Om true startas turen automatiskt om ej sedd
 */
export function useGuideTour(tourKey: string, autoStart = false): UseGuideTourReturn {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [joyrideKey, setJoyrideKey] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [hasSeenTour, setHasSeenTour] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    let cancelled = false;

    async function init() {
      try {
        const [prefs, tour] = await Promise.all([
          fetchPreferences(),
          fetchTour(tourKey),
        ]);

        if (cancelled) return;

        if (!tour || tour.steps.length === 0) {
          setIsReady(false);
          return;
        }

        const mappedSteps = tour.steps
          .filter((s) => s.is_active)
          .sort((a, b) => a.step_order - b.step_order)
          .map(mapToJoyrideStep);

        setSteps(mappedSteps);

        const seen = !!(prefs.guideTours?.[tourKey]);
        setHasSeenTour(seen);
        setIsReady(true);

        if (autoStart && !seen) {
          setRun(true);
        }
      } catch {
        // API-fel – guiden är inte kritisk, visa bara inte
        setIsReady(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [tourKey, autoStart]);

  const markAsSeen = useCallback(async () => {
    setHasSeenTour(true);
    try {
      await savePreferences({
        guideTours: { [tourKey]: new Date().toISOString() },
      });
    } catch {
      // Tyst fel – spara i preferences misslyckades
    }
  }, [tourKey]);

  const startTour = useCallback(() => {
    setJoyrideKey((k) => k + 1);
    setRun(true);
  }, []);

  const restartTour = useCallback(() => {
    setRun(false);
    setTimeout(() => {
      setJoyrideKey((k) => k + 1);
      setRun(true);
    }, 100);
  }, []);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status } = data;
      const finishedStatuses: Status[] = [STATUS.FINISHED, STATUS.SKIPPED];
      if (finishedStatuses.includes(status as Status)) {
        setRun(false);
        markAsSeen();
      }
    },
    [markAsSeen]
  );

  return {
    run,
    steps,
    joyrideKey,
    isReady,
    hasSeenTour,
    startTour,
    restartTour,
    handleJoyrideCallback,
  };
}
